import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { ChevronDown, ChevronUp, Tag, CheckCircle } from "lucide-react";

import Layout from "src/components/app/layout/Layout";
import Header from "src/components/app/layout/Header";
import Card from "src/components/app/Card/Card";
import Input from "src/components/app/Input";

import { useAppDispatch } from "src/redux/hooks";
import {
  checkDiscountRequest,
  coupanListDetailsRequest,
} from "src/redux/slices/offersSlice";
import { RootState } from "src/redux/store";
import { offers } from "src/types";

type CouponCardProps = {
  coupon: offers.CoupanData;
  totalAmount: number;
  index: number;
  onPressApply: (couponCode: string) => void;
  handleViewDetails: () => void;
  isApplied: boolean;
};

const CouponCard: React.FC<CouponCardProps> = ({
  coupon,
  totalAmount,
  index,
  onPressApply,
  handleViewDetails,
  isApplied,
}) => {
  const isDisabled: boolean =
    coupon.is_active === "N" || Number(coupon.min_cart_price) > totalAmount;
  const [showDetail, setShowDetail] = useState<string[]>([]);

  const toggleDetail = () => {
    if (isDisabled) return;

    setShowDetail((prev) =>
      prev.includes(coupon.discounts_master_id)
        ? prev.filter((id) => id !== coupon.discounts_master_id)
        : [...prev, coupon.discounts_master_id]
    );
  };

  return (
    <Card stackTitleAndDescription={true}>
      <div className="">
        <div className="flex items-center w-full">
          {!isDisabled ? (
            isApplied ? (
              <CheckCircle className="w-5 h-5 text-green-600" />
            ) : (
              <Tag
                width={20}
                height={20}
                //fill={colors.goodFlip.primary}
              />
            )
          ) : (
            <Tag width={20} height={20} className="text-gray-400" />
          )}

          <div className="flex flex-1 justify-between ml-2">
            <div>
              <p
                className={`font-semibold text-base ${
                  isDisabled
                    ? "text-gray-400"
                    : isApplied
                    ? "text-green-600"
                    : "text-gray-800"
                }`}
              >
                {coupon.coupon_name}
                {isApplied && (
                  <span className="ml-2 text-xs bg-green-100 text-green-600 px-2 py-1 rounded-full">
                    Applied
                  </span>
                )}
              </p>
              <p
                className={`text-sm ${
                  isDisabled
                    ? "text-gray-400"
                    : isApplied
                    ? "text-green-500"
                    : "text-gray-500"
                }`}
              >
                {coupon.label}
              </p>
            </div>

            <button
              onClick={() => onPressApply(coupon?.discount_code)}
              disabled={isDisabled || isApplied}
              className={`text-sm font-semibold ${
                isDisabled || isApplied
                  ? "text-gray-400 cursor-not-allowed"
                  : "text-primary hover:underline"
              }`}
            >
              {isApplied ? "Applied" : "Apply"}
            </button>
          </div>
        </div>

        <div className="border-t border-gray-200 my-3" />

        <p className="text-sm text-gray-600">{coupon?.onlydescription ?? ""}</p>

        <button
          onClick={toggleDetail}
          disabled={isDisabled}
          className={`mt-2 flex items-center text-sm font-medium ${
            isDisabled
              ? "text-gray-400 cursor-not-allowed"
              : isApplied
              ? "text-green-600 hover:underline"
              : "text-primary hover:underline"
          }`}
        >
          {showDetail.includes(coupon.discounts_master_id) ? (
            <>
              <span>Hide Details</span>
              <ChevronUp width={12} height={12} className="ml-1" />
            </>
          ) : (
            <>
              <span>View Details</span>
              {coupon.is_active ? (
                <ChevronDown width={12} height={12} className="ml-1" />
              ) : (
                <ChevronDown width={12} height={12} className="ml-1" />
              )}
            </>
          )}
        </button>
      </div>

      {showDetail.includes(coupon.discounts_master_id) && (
        <>
          <div className="border-gray-200 my-2" />
          <div className="px-4 pb-3 text-sm text-gray-700">
            <div
              dangerouslySetInnerHTML={{ __html: coupon.description }}
              className="prose prose-sm max-w-none"
            />
          </div>
        </>
      )}
    </Card>
  );
};

const ApplyCouponScreen: React.FC = () => {
  const params = useParams();
  const supportProgramKey = params["support-program-key"];
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [enteredCouponCode, setEnteredCouponCode] = useState<string>("");

  const couponData: offers.CoupanData[] | null = useSelector(
    (state: RootState) => state.offers.CoupanData?.data
  );

  const searchedCouponDiscount = useSelector(
    (state: RootState) => state.offers.CoupanDiscount?.data
  );

  const totalAmount = useSelector(
    (state: RootState) =>
      state.labTest.cartState?.data?.order_detail?.payable_amount
  );

  // Check if a coupon is currently applied
  const isCouponApplied = (coupon: offers.CoupanData): boolean => {
    return (
      searchedCouponDiscount?.discount_code?.discounts_master_id ===
        coupon.discounts_master_id ||
      searchedCouponDiscount?.discount_code?.discount_code ===
        coupon.discount_code
    );
  };

  useEffect(() => {
    getCoupanList();
  }, []);

  const getCoupanList = () => {
    const payload = {
      discount_type: "L",
      price: totalAmount,
    };
    new Promise((resolve, reject) => {
      dispatch(
        coupanListDetailsRequest({
          resolve,
          reject,
          payload,
        })
      );
    }).catch((err) => {
      console.error("Error fetching coupon list", err);
    });
  };

  const checkDiscountOnEnteredCouponCode = (
    existingCouponCode?: string,
    discounts_master_id?: string
  ) => {
    const payload = {
      price: Number(totalAmount),
      discount_code: existingCouponCode || enteredCouponCode,
      ...(discounts_master_id && { discounts_master_id }),
    };
    new Promise((resolve, reject) => {
      dispatch(
        checkDiscountRequest({
          payload,
          resolve,
          reject,
        })
      );
    })
      .then((res: any) => {
        // Navigate to checkout screen immediately after successful discount application
        if (res?.data) {
          navigate(`/app/${supportProgramKey}/book-lab-test/checkout`, {
            state: { showDiscountSuccess: true },
          });
        }
      })
      .catch((err) => {
        console.error("Error checking discount on entered coupon code", err);
      });
  };

  return (
    <Layout supportProgramKey={supportProgramKey || ""}>
      <div>
        <Header title="Apply Coupon" onBackClick={() => navigate(-1)} />
      </div>
      <div className="mt-2 p-4">
        <Input
          name="coupon"
          type="text"
          placeholder="Enter Coupon Code"
          endAdornment={
            <p
              className={`text-sm ${
                enteredCouponCode === ""
                  ? "cursor-not-allowed text-gray-400"
                  : "cursor-pointer text-[var(--text-primary)]"
              }`}
              onClick={() =>
                enteredCouponCode !== ""
                  ? checkDiscountOnEnteredCouponCode()
                  : null
              }
            >
              Apply
            </p>
          }
          fullWidth
          value={enteredCouponCode}
          onChange={(e) => setEnteredCouponCode(e.target.value)}
        />
      </div>
      <div className="mt-2 p-4 space-y-4">
        {couponData?.map((coupon, index) => (
          <CouponCard
            key={index}
            coupon={coupon}
            totalAmount={Number(totalAmount) || 0}
            index={index}
            onPressApply={(couponCode) => {
              checkDiscountOnEnteredCouponCode(
                couponCode,
                coupon?.discounts_master_id
              );
            }}
            handleViewDetails={() => {}}
            isApplied={isCouponApplied(coupon)}
          />
        ))}
      </div>
    </Layout>
  );
};

export default ApplyCouponScreen;
